import type { ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';

export interface TabItem<TValue extends string = string> {
  value: TValue;
  label: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
}

export interface TabsProps<TValue extends string = string> {
  items: readonly TabItem<TValue>[];
  value: TValue;
  onValueChange: (value: TValue) => void;
  /**
   * `pill` — aktywna zakladka wypelniona kolorem marki (jak nawigacja turnieju),
   * `underline` — aktywna zakladka podkreslona (jak zakladki ekranu klubowego).
   */
  variant?: 'pill' | 'underline';
  /** Etykieta grupy dla czytnikow ekranu. */
  label: string;
  className?: string;
}

/**
 * Zakladki nawigacyjne.
 *
 * Komponent jest w pelni sterowany — stan trzyma rodzic, dzieki czemu latwo
 * zsynchronizowac zakladki z adresem URL.
 */
export function Tabs<TValue extends string = string>({
  items,
  value,
  onValueChange,
  variant = 'pill',
  label,
  className,
}: TabsProps<TValue>) {
  return (
    <div
      role="tablist"
      aria-label={label}
      className={cn(
        'flex items-center gap-1 overflow-x-auto',
        variant === 'underline' && 'border-border gap-6 border-b',
        className,
      )}
    >
      {items.map((item) => {
        const isActive = item.value === value;

        return (
          <button
            key={item.value}
            type="button"
            role="tab"
            aria-selected={isActive}
            disabled={item.disabled}
            onClick={() => onValueChange(item.value)}
            className={cn(
              'inline-flex shrink-0 items-center gap-2 font-semibold whitespace-nowrap',
              'transition-colors duration-150 disabled:pointer-events-none disabled:opacity-45',
              '[&_svg]:size-4',
              variant === 'pill' && [
                'h-10 rounded-full px-4 text-sm',
                isActive
                  ? 'bg-brand text-fg-on-brand'
                  : 'text-fg-muted hover:bg-surface-hover hover:text-fg',
              ],
              variant === 'underline' && [
                'h-11 border-b-2 px-1 text-sm',
                isActive
                  ? 'border-brand text-fg-strong'
                  : 'hover:text-fg border-transparent text-fg-muted',
              ],
            )}
          >
            {item.icon}
            {item.label}
          </button>
        );
      })}
    </div>
  );
}
